<template>
  <div>
    <es-loader v-if="loading" style="margin: 6.5rem"/>
    <es-exception v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div v-else>
      <es-form :padding="{ padding: '1.15rem 1.15rem 0 1.15rem' }">
        <template #es-form>
          <div class="row removeUnderline">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  Type
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <div class="buttons has-addons">
                      <button
                        v-for="(item, index) in fileSearchTypeDisplayed"
                        :key="index"
                        class="button button--form"
                        @click="searchTypeChanged(item)"
                        :class="{'is-active': searchType.type === item.type}"
                        style="min-width: 100px"
                      >
                        {{ item.name }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if= "searchType.code === 'newSearch' || contextMode">
            <div class="row removeUnderline">
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">
                    {{ !contextMode ? 'Search Rule' : 'Person Type' }}
                  </label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      <div class="buttons has-addons">
                        <button
                          v-for="(item, index) in sortedPersonTypesOptions"
                          :key="index"
                          class="button button--form"
                          @click="personType = item.code"
                          :class="{'is-active': personType === item.code}"
                          :disabled="!enabledSubmissionStatus.includes(item.code)"
                          style="min-width: 100px"
                        >
                          {{ item.name }} {{ !contextMode ? 'Rule' : '' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if= "searchType.code === 'newSearch'">
            <div class="row removeUnderline">
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">
                    File to Upload
                  </label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      <!-- adding flex classes for help text below file input -->
                      <div
                        class="
                          is-flex
                          flex-direction--column
                          flex-justify--start
                          file
                          has-name
                          is-right
                          is-fullwidth
                        "
                      >
                        <es-file-upload-input
                          :has-error-file="hasErrorFile"
                          input-id="id"
                          input-ref="inputFile"
                          :selected-files="files"
                          @validFileExtensions="setErrors($event)"
                        />
                        <div v-if="hasErrorFile" class="help is-danger">
                          {{ errorMsgFile }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if= "searchType.code === 'prevSearch'">
            <div class="row removeUnderline">
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">
                    Date Range
                  </label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control is-flex" style="width: 280px;">
                      <input
                        class="input"
                        :class="{
                          'placeholder': !startDateInput,
                          'is-danger' : hasErrorStartDate
                        }"
                        type="date" aria-label="Date Range From"
                        v-model="startDateInput"
                        @change.prevent="validateField('startDate')"
                      >
                      <span
                        class="is-flex flex-align--center"
                        style="padding: 0 0.5rem; font-size: 12px"
                      >
                        to
                      </span>
                      <input
                        class="input calendar"
                        :class="{
                          'placeholder': !endDateInput,
                          'is-danger' : hasErrorEndDate
                        }"
                        type="date" aria-label="Date Range To"
                        v-model="endDateInput"
                        @change.prevent="validateField('endDate')"
                      >
                    </div>
                    <div class="is-flex">
                      <span v-if ="hasErrorStartDate" class="help is-danger">
                        {{ msgStartDate }}
                      </span>
                      <span v-if ="hasErrorEndDate" class="help is-danger"
                        style="padding-left: 11rem; position: absolute">
                        {{ msgEndDate }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row removeUnderline">
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">
                    Search Number (optional)
                  </label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control" style="width: 500px;">
                      <input
                        class="input"
                        :class="{'is-danger' : hasErrorSearchNumber}"
                        @keyup.prevent="validateField('searchNumber')"
                        type="text" aria-label="Search Number"
                        v-model="searchNumberInput"
                      >
                      <span v-if ="hasErrorSearchNumber" class="help is-danger">
                        {{ msgSearchNumber }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </es-form>
      <pid-advanced-upload-format :formOptions="advancedFormOptions"
        v-if= "searchType.code === 'newSearch'"/>
      <div
        class="is-flex flex-justify--end flex-align--center"
        style="margin-right: 5.15rem; padding-bottom: 2rem"
      >
        <button
          class="button button-link has-underline"
          @click="reset"
          style="margin-right: 1rem"
        >
          Reset
        </button>
        <button
          class="button is-primary"
          :disabled="loading || !isPersonTypeEnabled"
          @click="checkForm"
        >
        <!-- {{ properties['BUTTON_LABEL-ASSIGN_EXISTING'] }} -->
          Search
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import EsFileUploadInput from '@/components/EsFileUploadInput.vue';
import EsForm from '@/components/EsForm.vue';
import EsException from '@/components/EsException.vue';
import DateUtils from '@/mixins/DateUtils';
import EsLoader from '@/components/EsLoader.vue';
import PidAdvancedUploadFormat from '@/components/PidAdvancedUploadFormat.vue';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

export default {
  mixins: [
    FieldLabels,
    DateUtils,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsFileUploadInput,
    EsForm,
    EsException,
    EsLoader,
    PidAdvancedUploadFormat,
  },
  props: {
    sortField: {
      type: String,
      default: 'id',
    },
    sortOrder: {
      type: String,
      default: 'ASC',
    },
    page: {
      type: String,
    },
  },
  data() {
    return {
      allSubmissionSpecs: [],
      advancedFormOptions: {},
      searchNumberInput: null,
      messages: [],
      errorMsgFile: null,
      files: [],
      hasErrorFile: false,
      loading: false,
      loadingError: null,
      loadingSearch: false,
      loadingSearchError: null,
      personType: null,
      personTypeOptions: [],
      startDateInput: '',
      endDateInput: '',
      hasErrorSearchNumber: false,
      msgSearchNumber: null,
      hasErrorStartDate: false,
      msgStartDate: null,
      hasErrorEndDate: false,
      msgEndDate: null,
      searchType: null,
      searchTypeOptions: [
        {
          name: 'New Search File ',
          code: 'newSearch',
          type: 'newSearch',
          permission: 'PRSID:SRCH:OPTIONS:BATCH:SRCH',
        },
        {
          name: 'Find My Search Files',
          code: 'prevSearch',
          type: 'searchFiles',
          permission: 'PRSID:SRCH:OPTIONS:BATCH:SRCH',
        },
        {
          name: 'Find My Location(s) Files',
          code: 'prevSearch',
          type: 'locationFiles',
          permission: 'PRSID:SRCH:OPTIONS:BATCH:SRCH',
        },
      ],
      userPersonTypesCodes: null,
    };
  },
  created() {
    this.setAdvancedFormOptions();
  },
  beforeMount() {
    // eslint-disable-next-line prefer-destructuring
    this.searchType = this.fileSearchTypeDisplayed[0];
  },
  async mounted() {
    this.loading = true;
    this.loadingError = false;
    this.$emit('fileSearchType', this.searchType);
    try {
      await Promise.all([
        this.loadSources(),
        this.loadPersonTypes(),
      ]);
      this.sourceInput = this.defaultSource;
      this.userPersonTypesCodes = this.userPersonTypes
        .map(item => item.code);
      if (this.sortedPersonTypesOptions.length === 1) {
        this.personType = this.sortedPersonTypesOptions[0].code;
      } else if (this.searchType.code === 'newSearch' || this.contextMode) {
        this.personType = this.userPersonTypesCodes.includes(this.defaultPersonType)
          ? this.defaultPersonType : this.userPersonTypesCodes[0];
      }
      this.startDateInput = this.buildDateString(29);
      this.loadSubmissionSpecs();
    } catch (err) {
      this.loadingError = true;
      this.messages.push('There were some errors. Please try again. If errors persist please contact support.');
      this.showNotification('danger', this.messages);
    } finally {
      this.loading = false;
    }
  },
  watch: {
    uploadType() {
      if (this.uploadType === 'SIMPLE') {
        this.setAdvancedFormOptions();
      }
    },
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters('lookups', [
      'activeSources',
      'defaultPersonType',
    ]),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    allSubSpecsArray() {
      return [].concat(...this.allSubmissionSpecs);
    },
    defaultSource() {
      if (this.activeSources.length) {
        const defaultType = this.activeSources
          .find(el => el.defaultType === true);
        return defaultType ? defaultType.code : this.activeSources[0].code;
      } return null;
    },
    isPersonTypeEnabled() {
      return !this.contextMode
        || (this.enabledSubmissionStatus.includes(this.personType) && (this.personType !== null));
    },
    enabledSubmissionStatus() {
      return this.allSubSpecsArray
        .filter(d => d.purpose === 'SEARCH' && d.enabled === true)
        .map(p => p.personType.code);
    },
    fileSearchTypeDisplayed() {
      return this.searchTypeOptions.filter(type => this.hasModule(type.permission));
    },
    formDataBody() {
      const formData = new FormData();
      // TODO: currently the submissionTypeCode is hardcoded and while it will always be FILE
      //   we may want to load submission types here and use that
      formData.append('batchType', 'SEARCH');
      formData.append('submissionTypeCode', 'FILE');
      formData.append('personTypeCode', this.personType);
      formData.append('file', this.files[0]);
      formData.append('uploadType', this.advancedFormOptions.uploadType);
      if (this.advancedFormOptions.uploadType === 'ADVANCED') {
        formData.append('fileVersion', this.advancedFormOptions.fileFormat);
        formData.append('delimiter', this.advancedFormOptions.delimiter);
        formData.append('qualifier', this.advancedFormOptions.qualifier);
        formData.append('sourceCode', this.advancedFormOptions.sourceInput.length > 0
          ? this.advancedFormOptions.sourceInput[0].code : null);
        formData.append('ignoreFirstRow', this.advancedFormOptions.ignoreFirstRow);
      }
      return formData;
    },
    searchParams() {
      const params = {};
      if (this.searchType.code === 'newSearch' || this.contextMode) {
        params.personType = this.personType ? this.personType : undefined;
      }
      if (this.searchNumberInput) {
        params.id = this.searchNumberInput ? this.searchNumberInput.trim() : undefined;
      } else {
        params.startDate = this.startDateInput ? this.startDateInput : undefined;
        params.endDate = this.endDateInput ? this.endDateInput : undefined;
      }
      return params;
    },
    sortedPersonTypesOptions() {
      return this.userPersonTypes.slice().sort((a, b) => {
        if (a.name.toUpperCase() > b.name.toUpperCase()) {
          return 1;
        }
        if (a.name.toUpperCase() < b.name.toUpperCase()) {
          return -1;
        }
        return 0;
      });
    },
    uploadType() {
      return this.advancedFormOptions.uploadType;
    },
    contextMode() {
      const identityControl = this.properties['IDENTITY_CONTROL-LIFETIME_IDS'];
      return JSON.parse(identityControl.toString().toLowerCase()) === false;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadSources',
      'loadPersonTypes',
    ]),
    checkForm() {
      this.clearPrevSearchErrors();
      this.clearFileErrors();
      if (this.searchType.code === 'newSearch') {
        this.validateUploadFileField();
      }
      if (this.searchType.code === 'prevSearch') {
        this.validateField('searchNumber');
        this.validateField('startDate');
        this.validateField('endDate');
      }
      const err = this.hasErrorSearchNumber || this.hasErrorStartDate
        || this.hasErrorEndDate || this.hasErrorFile;
      if (err) {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
      } else {
        this.search();
      }
    },
    async loadSubmissionSpecs() {
      const personTypeIds = this.userPersonTypes.map(item => item.id);
      try {
        this.loading = true;
        this.loadingError = false;
        // build promises to get submission specs info per person type
        const submissionSpecResponse = await Promise.all([...personTypeIds]
          .map(id => axios.get(`person-types/${id}/submission-specs/`)));
        // use the response from above and push them
        submissionSpecResponse.forEach((s) => {
          this.allSubmissionSpecs.push(s.data);
        });
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    validateField(field) {
      if (field === 'searchNumber') {
        if (this.searchNumberInput && this.searchNumberInput.length > 15) {
          this.hasErrorSearchNumber = true;
          this.msgSearchNumber = 'Search Number must be less than 15 characters.';
        } else {
          this.hasErrorSearchNumber = false;
          this.msgSearchNumber = null;
        }
      }
      if (field === 'startDate') {
        this.hasErrorStartDate = false;
        if (!this.startDateInput && !this.searchNumberInput) {
          this.hasErrorStartDate = true;
          this.msgStartDate = 'Start Date is required.';
        } else if (this.startDateInput
           && !this.isValidDateFormat(this.startDateInput, 'YYYY-MM-DD')) {
          this.hasErrorStartDate = true;
          this.msgStartDate = 'Start Date is invalid.';
        } else if (this.endDateInput) {
          if (moment(this.startDateInput).isAfter(this.endDateInput)) {
            this.hasErrorStartDate = true;
            this.msgStartDate = 'Start Date cannot be after End Date.';
          }
        } else {
          const today = new Date().getTime();
          if (moment(this.startDateInput).isAfter(today)) {
            this.hasErrorStartDate = true;
            this.msgStartDate = 'Start Date cannot be future date.';
          } else {
            this.hasErrorStartDate = false;
            this.msgStartDate = null;
          }
        }
      }
      if (field === 'endDate') {
        this.hasErrorEndDate = false;
        if (this.endDateInput
            && !this.isValidDateFormat(this.endDateInput, 'YYYY-MM-DD')) {
          this.hasErrorEndDate = true;
          this.msgEndDate = 'End Date is invalid.';
        } else if (moment(this.startDateInput).isAfter(this.endDateInput)) {
          this.hasErrorEndDate = true;
          this.msgEndDate = 'End Date cannot be before Start Date.';
        } else {
          const today = new Date().getTime();
          if (moment(this.endDateInput).isAfter(today)) {
            this.hasErrorEndDate = true;
            this.msgEndDate = 'End Date cannot be future date.';
          } else {
            this.hasErrorEndDate = false;
            this.msgEndDate = null;
          }
        }
      }
    },
    validateUploadFileField() {
      if (!this.files.length) {
        this.hasErrorFile = true;
        this.errorMsgFile = 'No file chosen. A File is required to Upload.';
      } else {
        this.hasErrorFile = false;
        this.errorMsgFile = '';
      }
    },
    search() {
      const query = {};
      if (this.searchType.code === 'newSearch') {
        query.personType = this.personType;
        query.uploadType = this.advancedFormOptions.uploadType;
      } else if (this.searchType.code === 'prevSearch') {
        if (this.searchNumberInput) {
          query.id = this.searchNumberInput;
        } else {
          query.startDate = this.startDateInput;
          query.endDate = this.endDateInput;
        }
        if (this.personType) query.personType = this.personType;
      }
      this.$router.replace({ query: {} });
      this.$router.push({ name: 'PidSearchFile', query });

      this.$emit('formDataBody', this.formDataBody);
      this.$emit('searchParams', this.searchParams);
      this.$emit('show', false);
    },
    clearErrors() {
      this.hideNotification();
      if (this.searchType.code === 'newSearch') {
        this.clearFileErrors();
      } else if (this.searchType.code === 'prevSearch') {
        this.clearPrevSearchErrors();
      }
    },
    clearFileErrors() {
      this.hasErrorFile = false;
      this.errorMsgFile = null;
    },
    clearPrevSearchErrors() {
      this.hasErrorSearchNumber = false;
      this.hasErrorStartDate = false;
      this.hasErrorEndDate = false;
      this.msgSearchNumber = null;
      this.msgStartDate = null;
      this.msgEndDate = null;
    },
    reset() {
      this.$router.replace({ query: {} }).catch(() => {});
      this.files = [];
      this.searchNumberInput = '';
      this.startDateInput = '';
      this.endDateInput = '';
      if (this.sortedPersonTypesOptions.length === 1) {
        this.personType = this.sortedPersonTypesOptions[0].code;
      } else {
        this.personType = this.userPersonTypesCodes.includes(this.defaultPersonType)
          ? this.defaultPersonType : this.userPersonTypesCodes[0];
      }
      this.searchType = this.fileSearchTypeDisplayed.find(t => t.code === 'newSearch') || this.fileSearchTypeDisplayed[0];
      this.advancedFormOptions.uploadType = 'SIMPLE';
      // this.searchParams();
      this.startDateInput = this.buildDateString(29);
      this.$emit('show', true);
      this.clearErrors();
    },
    setAdvancedFormOptions() {
      this.advancedFormOptions = {
        batchType: 'SEARCH',
        uploadType: 'SIMPLE',
        fileFormat: '3.0',
        delimiter: 'TAB',
        qualifier: 'NONE',
        sourceInput: [{ code: this.defaultSource }],
        ignoreFirstRow: false,
      };
    },
    setErrors(value) {
      if (!value) {
        this.hasErrorFile = true;
        this.errorMsgFile = 'File extension is invalid. Valid extensions are \'.txt\' or \'.csv\' or \'.TXT\' or \'.CSV\'';
      } else {
        this.clearErrors();
      }
    },
    searchTypeChanged(searchType) {
      this.searchType = { ...searchType };
      this.$emit('fileSearchType', searchType);
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
